import React, { useCallback, useEffect, useState } from 'react';
import { ExplainMetricButton } from '../../features/abf/copilot/AbfCopilotLayout';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { INK } from './shared/creditReviewStyles';
import { iborPositionPath, leveragedLoanTicketPath, loan360Path, securityMasterPath } from '../../utils/loanNavigation';
import {
  ControlRoomDrawer,
  DataTable,
  DrawerBackdrop,
  DrawerHeader,
  EmptyStateBox,
  EvidenceChip,
  InfoCard,
  InfoGrid,
  InfoLabel,
  InfoValue,
  MetricCard,
  MetricLabel,
  MetricStrip,
  MetricSubtext,
  MetricValue,
  StatusPill,
  TableShell,
  Timeline,
  TimelineItem,
  WorkspaceButton,
  moneyCompact,
} from './shared/CreditWorkspace';

// AXP-128 (FRD v1.3 LL-FR-2, §5): Loan 360 facility card drawer.
//
// Two hard-separated sections (control-room restyle, semantics unchanged):
//   1. "Deal & Facilities (reference data)" — the deal header + per-tranche table
//      from dim_credit_deal / dim_leveraged_loan reference columns, with the
//      evidence provenance chip ("as of {date} · {source}") and an explicit
//      "Awaiting agent evidence" state when global outstanding is null.
//   2. "Your portfolio exposure" — the fund's own numbers, derived by the
//      backend EXCLUSIVELY from fact_positions/typed attrs and
//      fact_trade_transaction. TRADE vs SETTLE basis is visibly distinguished:
//      pending items are labeled "pending settlement — forecast", never booked.
//
// Every number comes from GET /api/credit/loans/{id}/facility-card.
// D4 (p1-consolidation-decisions.md): settlement mutations live on the Middle
// Office Loan Settlement queue; this card stays read-only status + deep links.

interface TrancheRow {
  loanRecordId: number;
  trancheCode?: string | null;
  facilityType?: string | null;
  cusip?: string | null;
  originalSize?: number | null;
  currentCapacity?: number | null;
  globalOutstanding?: number | null;
  availability?: number | null;
  spreadBps?: number | null;
  baseRate?: string | null;
  allInRate?: number | null;
  floorRate?: number | null;
  oidPrice?: number | null;
  maturityDate?: string | null;
  facilityState?: string | null;
  outstandingSource?: string | null;
  outstandingAsOf?: string | null;
  reborrowable?: boolean;
  // Jackson may serialize the `isSubject` record component as either name.
  isSubject?: boolean;
  subject?: boolean;
}

interface DealPanel {
  dealId?: number | null;
  dealCode?: string | null;
  dealName?: string | null;
  borrowerName?: string | null;
  sponsorName?: string | null;
  agentBank?: string | null;
  agreementDate?: string | null;
  dealStatus?: string | null;
  source?: string | null;
  sourceRef?: string | null;
  documentEvidence?: Array<{
    jobId: string;
    documentRole?: string | null;
    documentHash?: string | null;
    documentName?: string | null;
    reviewer?: string | null;
    reviewedAt?: string | null;
    payloadHash?: string | null;
  }>;
  tranches: TrancheRow[];
}

interface ExposureTotals {
  fundedPrincipal: number;
  unfundedCommitment: number;
  settledExposure: number;
  pendingBuy: number;
  pendingSell: number;
}

interface PortfolioExposureRow extends ExposureTotals {
  portfolioId: number;
  portfolioCode?: string | null;
  portfolioName?: string | null;
  positionDate?: string | null;
}

interface PendingTradeRow {
  tradeId: number;
  portfolioId?: number | null;
  portfolioCode?: string | null;
  side: 'BUY' | 'SELL';
  par: number;
  price?: number | null;
  tradeDate?: string | null;
  expectedSettleDate?: string | null;
  settlementStatus?: string | null;
  legalTransferStatus?: string | null;
  settlementConvention?: string | null;
  settlementExceptionReason?: string | null;
  basis: string;
}

interface DataQualityException {
  positionId?: number | null;
  portfolioCode?: string | null;
  field?: string | null;
  message: string;
}

interface PortfolioPanel {
  aggregate: ExposureTotals;
  portfolios: PortfolioExposureRow[];
  pendingTrades: PendingTradeRow[];
  dataQualityExceptions: DataQualityException[];
  holdingsExist: boolean;
}

interface FacilityCardPayload {
  loanId: number;
  asOfDate?: string | null;
  dealPanel: DealPanel;
  portfolioPanel: PortfolioPanel;
}

// F24: date-only strings ("YYYY-MM-DD") must be parsed as LOCAL dates —
// new Date("YYYY-MM-DD") is UTC midnight and renders off-by-one in western TZs.
export const parseLocalDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (value?: string | null) => {
  const d = parseLocalDate(value);
  return d ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '--';
};
const fmtMoney = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};
const fmtRate = (value?: number | null, suffix = '%') =>
  value === null || value === undefined ? '--' : `${Number(value).toFixed(2)}${suffix}`;

const stateTone = (state?: string | null): 'emerald' | 'amber' | 'slate' => {
  switch (state) {
    case 'ACTIVE': return 'emerald';
    case 'EXPECTED':
    case 'NOT_EFFECTIVE': return 'amber';
    default: return 'slate';
  }
};

const SectionTitle: React.FC<{ children: React.ReactNode; description?: string }> = ({ children, description }) => (
  <div style={{ margin: '1.2rem 0 0.65rem' }}>
    <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '0.95rem' }}>{children}</h3>
    {description && <div style={{ color: '#64748b', fontSize: '0.74rem', marginTop: '0.25rem' }}>{description}</div>}
  </div>
);


interface Props {
  loanId: number;
  loanName?: string;
  onClose: () => void;
  embedded?: boolean;
  view?: 'facility' | 'settlement';
  securityId?: number;
}

const InlineFacility = styled.section`
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  ${DataTable} { min-width: 850px; }
  ${DataTable} th { white-space: nowrap; }
  ${DataTable} td { vertical-align: middle; padding-top: .85rem; padding-bottom: .85rem; }
  ${MetricStrip} { grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0; padding: 18px 20px; border: 1px solid ${INK.line}; border-radius: 12px; background: ${INK.panel}; margin: 0 0 20px; }
  ${MetricCard} { border: 0; border-radius: 0; background: transparent; box-shadow: none; padding: 0 16px; border-left: 1px solid ${INK.line}; &:first-child { border-left: 0; padding-left: 0; } }
  ${MetricCard}::before, ${MetricCard}::after { display: none; }
  ${InfoCard} { border: 0; border-radius: 0; background: transparent; box-shadow: none; padding: 12px 0; }
`;

const FacilityReview = styled.section`
  padding: 24px; margin: 0 0 20px; border: 1px solid ${INK.line}; border-radius: 12px;
  background: ${INK.panel}; color: ${INK.body}; min-width: 0;
  .section-top { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
  h3 { margin: 4px 0 0; font-size: 17px; color: ${INK.strong}; }
  .eyebrow { color: ${INK.accent}; font-size: 11px; text-transform: uppercase; letter-spacing: .08em; }
  .muted, small, p { color: ${INK.muted}; font-size: 13px; line-height: 1.6; }
  .facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 24px; margin: 22px 0; }
  dt { color: ${INK.muted}; font-size: 12px; margin-bottom: 7px; }
  dd { margin: 0; font-size: 15px; font-weight: 600; color: ${INK.strong}; overflow-wrap: anywhere; }
  small { display: block; margin-top: 5px; }
  .pending { color: #fcd34d; font-size: 13px; font-weight: 500; }
  .badges, .record-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .tranche { padding-top: 20px; }
  .tranche + .tranche { margin-top: 20px; border-top: 1px solid ${INK.line}; }
  && .evidence { padding-top: 14px; border-top: 1px solid ${INK.line}; }
  .evidence summary { cursor: pointer; font-size: 13px; color: ${INK.soft}; }
  .evidence summary span { color: ${INK.muted}; margin-left: 12px; font-weight: 400; }
  .evidence p, .document { margin-top: 14px; }
  .document strong { font-size: 14px; }
  .record-actions { margin-top: 16px; }
  ${EmptyStateBox} { text-align: left; padding: 18px 0; border: 0; background: transparent; font-size: 14px; }
  @media(max-width: 1050px) { .facts { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media(max-width: 600px) { padding: 18px; .facts { grid-template-columns: 1fr; gap: 16px; } .section-top { flex-wrap: wrap; } }
`;

const LoanFacilityCardDrawer: React.FC<Props> = ({ loanId, loanName, onClose, embedded = false, view = 'facility', securityId }) => {
  const navigate = useNavigate();
  const [card, setCard] = useState<FacilityCardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCard = useCallback(async (showSpinner: boolean) => {
    try {
      if (showSpinner) setLoading(true);
      setError(null);
      const resp = await axios.get(`/api/credit/loans/${loanId}/facility-card`);
      setCard(resp.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load facility card');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [loanId]);

  useEffect(() => { loadCard(true); }, [loadCard]);

  const deal = card?.dealPanel;
  const exposure = card?.portfolioPanel;
  // An unlinked embedded loan cannot establish whether holdings are actually zero.
  const exposureUnavailable = embedded && !securityId;
  const subjectTranche = deal?.tranches.find(t => t.isSubject ?? t.subject);
  const evidence = subjectTranche ?? deal?.tranches[0];
  const hasEvidence = evidence?.globalOutstanding !== null && evidence?.globalOutstanding !== undefined;
  const noExposure = !exposureUnavailable && !!exposure && !exposure.holdingsExist && exposure.pendingTrades.length === 0;
  const goTo = (path: string) => { onClose(); navigate(path); };

  const pendingCount = exposure?.pendingTrades.length ?? 0;
  const failedSettlements = (exposure?.pendingTrades ?? []).filter(t => t.settlementStatus === 'FAILED').length;
  const dqCount = exposure?.dataQualityExceptions.length ?? 0;
  const attentionCount = pendingCount + dqCount;
  const attentionTone: 'emerald' | 'amber' | 'red' = exposureUnavailable ? 'amber' : attentionCount === 0 ? 'emerald' : failedSettlements > 0 ? 'red' : 'amber';
  const Frame = embedded ? InlineFacility : ControlRoomDrawer;
  const showFacility = !embedded || view === 'facility';

  return (
    <>
      {!embedded && <DrawerBackdrop onClick={onClose} />}
      <Frame>
        {!embedded && <DrawerHeader>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#38bdf8', fontSize: 11, fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.16em' }}>
              Leveraged Loan Facility Card
            </div>
            <h2 style={{ margin: '0.25rem 0 0', color: '#f8fafc', fontSize: '1.35rem' }}>
              {loanName || deal?.dealName || `Loan #${loanId}`}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
              {deal?.borrowerName && <span style={{ color: '#94a3b8' }}>{deal.borrowerName}</span>}
              {deal?.agentBank && <span style={{ color: '#64748b', fontSize: 12 }}>Agent {deal.agentBank}</span>}
              {deal?.dealStatus && (
                <StatusPill $tone={deal.dealStatus === 'ACTIVE' ? 'emerald' : deal.dealStatus === 'EXPECTED' ? 'amber' : 'slate'}>
                  {deal.dealStatus}
                </StatusPill>
              )}
              {hasEvidence ? (
                <EvidenceChip $tone="cyan">
                  as of {fmtDate(evidence?.outstandingAsOf)} · {evidence?.outstandingSource || 'UNKNOWN SOURCE'}
                </EvidenceChip>
              ) : (
                <EvidenceChip $tone="amber">Awaiting agent evidence</EvidenceChip>
              )}
            </div>
          </div>
          <WorkspaceButton onClick={onClose}>Close</WorkspaceButton>
        </DrawerHeader>}

        {embedded && <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <div><h2 style={{ margin: 0, fontSize: '1.1rem', color: '#f8fafc' }}>{view === 'facility' ? 'Facility & Exposure' : 'Lots & Settlement'}</h2>
            <p style={{ color: '#94a3b8', fontSize: '.78rem', marginBottom: 0 }}>{view === 'facility' ? 'Deal reference and your portfolio exposure, clearly separated.' : 'Review open lots, pending transfers, and settlement exceptions in one place.'}</p></div>
          <WorkspaceButton disabled={loading} onClick={() => loadCard(true)}>Refresh</WorkspaceButton>
        </div>}

        {loading && <EmptyStateBox>Loading facility card...</EmptyStateBox>}
        {error && <EmptyStateBox style={{ color: '#fca5a5' }}>{error}</EmptyStateBox>}

        {!loading && !error && card && deal && exposure && (
          <>
            <MetricStrip>
              <MetricCard $tone="blue">
                <MetricLabel>Funded Principal <ExplainMetricButton scope="current" metric="FUNDED" /></MetricLabel>
                <MetricValue>{exposureUnavailable ? 'Unavailable' : moneyCompact(exposure.aggregate.fundedPrincipal)}</MetricValue>
                <MetricSubtext>trade-date basis, posted trades</MetricSubtext>
              </MetricCard>
              <MetricCard $tone="cyan">
                <MetricLabel>Unfunded Commitment <ExplainMetricButton scope="current" metric="UNFUNDED" /></MetricLabel>
                <MetricValue>{exposureUnavailable ? 'Unavailable' : moneyCompact(exposure.aggregate.unfundedCommitment)}</MetricValue>
                <MetricSubtext>{exposureUnavailable ? 'Security link required' : `Across ${exposure.portfolios.length} portfolios`}</MetricSubtext>
              </MetricCard>
              <MetricCard $tone="emerald">
                <MetricLabel>Settled Exposure <ExplainMetricButton scope="current" metric="SETTLED_EXPOSURE" /></MetricLabel>
                <MetricValue>{exposureUnavailable ? 'Unavailable' : moneyCompact(exposure.aggregate.settledExposure)}</MetricValue>
                <MetricSubtext>settle-date basis, actual-settled lots only</MetricSubtext>
              </MetricCard>
              <MetricCard $tone={attentionTone}>
                <MetricLabel>Attention <ExplainMetricButton scope="current" metric="ATTENTION" /></MetricLabel>
                <MetricValue>{exposureUnavailable ? 'Unavailable' : attentionCount === 0 ? 'Clear' : attentionCount}</MetricValue>
                <MetricSubtext>
                  {exposureUnavailable ? 'Security link required' : attentionCount === 0
                    ? 'No pending settlements or exceptions'
                    : `${pendingCount} unsettled · ${dqCount} data-quality`}
                </MetricSubtext>
              </MetricCard>
            </MetricStrip>

            {/* ============ Section 1: FACILITY (REFERENCE DATA) ============ */}
            {showFacility && <>
            <FacilityReview>
              <div className="section-top"><div><span className="eyebrow">Facility reference</span><h3>Borrower & agreement</h3></div>
                <span className="muted">Across all lenders</span></div>
              <dl className="facts">
                <div><dt>Borrower</dt><dd>{deal.borrowerName || 'Not recorded'}</dd></div>
                <div><dt>Agent bank</dt><dd>{deal.agentBank || 'Not recorded'}</dd></div>
                <div><dt>Sponsor</dt><dd>{deal.sponsorName || 'Not recorded'}</dd></div>
                <div><dt>Agreement date</dt><dd>{fmtDate(deal.agreementDate)}</dd></div>
              </dl>
              <details className="evidence"><summary>Agreement evidence <span>{deal.documentEvidence?.length || 0} documents</span></summary>
                <p>Deal reference: {deal.dealCode || deal.dealName || 'Not recorded'} · Source: {deal.source || 'Not recorded'}</p>
                {!deal.documentEvidence?.length && <p>No reviewed agreement documents are available.</p>}
                {(deal.documentEvidence || []).map(row => <div className="document" key={row.jobId}>
                  <strong>{row.documentName || row.documentRole || row.jobId}</strong>
                  <p>Reviewed by {row.reviewer || 'System workflow'}{row.documentHash ? ' · SHA-256 ' + row.documentHash.slice(0, 12) + '…' : ''}</p>
                </div>)}
              </details>
            </FacilityReview>
            <FacilityReview>
              <div className="section-top"><div><span className="eyebrow">Contract terms</span><h3>Facilities & tranches</h3></div>
                <span className="muted">{deal.tranches.length} {deal.tranches.length === 1 ? 'facility' : 'facilities'}</span></div>
              {deal.tranches.length === 0 && <p>No facility terms are available.</p>}
              {deal.tranches.map(t => {
                const isSubject = t.isSubject ?? t.subject ?? t.loanRecordId === loanId;
                return <div className="tranche" key={t.loanRecordId}>
                  <div className="section-top"><strong>{(t.trancheCode || t.facilityType || 'Facility').replace(/_/g, ' ')}</strong>
                    <div className="badges">{isSubject && <StatusPill $tone="cyan">This loan</StatusPill>}
                      <StatusPill $tone={stateTone(t.facilityState)}>{t.facilityState || 'Not recorded'}</StatusPill></div>
                  </div>
                  <dl className="facts">
                    <div><dt>Capacity {isSubject && <ExplainMetricButton scope="current" metric="CAPACITY" />}</dt><dd>{fmtMoney(t.currentCapacity)}</dd></div>
                    <div><dt>Pricing {isSubject && <ExplainMetricButton scope="current" metric="ALL_IN_RATE" />}</dt><dd>{t.baseRate || 'Index not recorded'} + {fmtRate(t.spreadBps, ' bps')}</dd><small>All-in {fmtRate(t.allInRate)}</small></div>
                    <div><dt>Maturity</dt><dd>{fmtDate(t.maturityDate)}</dd></div>
                    <div><dt>Global outstanding</dt><dd>{t.globalOutstanding != null ? fmtMoney(t.globalOutstanding) : <span className="pending">Awaiting agent evidence</span>}</dd>
                      {t.outstandingAsOf && <small>As of {fmtDate(t.outstandingAsOf)} · {t.outstandingSource || 'Source not recorded'}</small>}</div>
                  </dl>
                  <details className="evidence"><summary>Additional terms & records</summary>
                    <dl className="facts">
                      <div><dt>Type</dt><dd>{(t.facilityType || 'Not recorded').replace(/_/g, ' ')}</dd></div>
                      <div><dt>Original size</dt><dd>{fmtMoney(t.originalSize)}</dd></div>
                      <div><dt>Availability</dt><dd>{fmtMoney(t.availability)}</dd></div>
                      <div><dt>OID price</dt><dd>{fmtRate(t.oidPrice, '')}</dd></div>
                    </dl>
                    <div className="record-actions">
                      {!isSubject && <WorkspaceButton onClick={() => goTo(loan360Path('LEVERAGED_LOAN', t.loanRecordId))}>Open Loan 360</WorkspaceButton>}
                      {t.cusip && <WorkspaceButton onClick={() => goTo(securityMasterPath(t.cusip!))}>Security Master</WorkspaceButton>}
                      <WorkspaceButton onClick={() => goTo(leveragedLoanTicketPath(t.cusip || undefined))}>Create acquisition ticket</WorkspaceButton>
                      <WorkspaceButton onClick={() => goTo(iborPositionPath(t.cusip || t.trancheCode || String(t.loanRecordId)))}>Open IBOR</WorkspaceButton>
                    </div>
                  </details>
                </div>;
              })}
            </FacilityReview>

            {/* ============ Section 2: YOUR PORTFOLIO EXPOSURE ============ */}
            <FacilityReview>
            <div className="section-top"><div><span className="eyebrow">Your investment</span><h3>Holdings &amp; settlement</h3></div>
              <WorkspaceButton disabled={exposureUnavailable} title={exposureUnavailable ? 'A linked security is required to open loan holdings.' : 'Review detailed positions and lots'}
                onClick={() => goTo(iborPositionPath(subjectTranche?.cusip || loanName || String(loanId)))}>Open IBOR</WorkspaceButton>
            </div>
            <p style={{ marginTop: 8 }}>Portfolio positions as of {fmtDate(card.asOfDate)}. Pending trades are shown separately from settled exposure.</p>

            {exposureUnavailable ? (
              <EmptyStateBox role="status">Unavailable — security link required.
                <div style={{ marginTop: 8 }}>Link this loan to a Security Master record before reviewing holdings and settled exposure.</div>
                <WorkspaceButton style={{ marginTop: 12 }} onClick={() => goTo(securityMasterPath(subjectTranche?.cusip || loanName || String(loanId)))}>Open Security Master</WorkspaceButton>
              </EmptyStateBox>
            ) : noExposure ? (
              <EmptyStateBox>
                No portfolio holdings returned.
                <div style={{ fontSize: '0.8rem', marginTop: 6, color: '#94a3b8' }}>
                  This facility has no holdings or pending trades in the returned portfolio records.
                </div>
              </EmptyStateBox>
            ) : (
              <>
                <InfoGrid style={{ marginTop: 10 }}>
                  <InfoCard style={{ borderColor: 'rgba(251, 191, 36, 0.3)' }}>
                    <InfoLabel>Pending Buy — Forecast</InfoLabel>
                    <InfoValue style={{ color: '#fcd34d' }}>{fmtMoney(exposure.aggregate.pendingBuy)}</InfoValue>
                  </InfoCard>
                  <InfoCard style={{ borderColor: 'rgba(251, 191, 36, 0.3)' }}>
                    <InfoLabel>Pending Sell — Forecast</InfoLabel>
                    <InfoValue style={{ color: '#fcd34d' }}>{fmtMoney(exposure.aggregate.pendingSell)}</InfoValue>
                  </InfoCard>
                </InfoGrid>

                {exposure.portfolios.length > 0 && (
                  <TableShell style={{ marginTop: 12 }}>
                    <div style={{ overflowX: 'auto' }}>
                      <DataTable>
                        <thead>
                          <tr>
                            <th>Portfolio</th>
                            <th>Trade-Date Funded</th>
                            <th>Unfunded</th>
                            <th>Settle-Date Exposure</th>
                            <th>Pending Buy</th>
                            <th>Pending Sell</th>
                            <th>As Of</th>
                          </tr>
                        </thead>
                        <tbody>
                          {exposure.portfolios.map(p => (
                            <tr key={p.portfolioId}>
                              <td style={{ fontWeight: 700 }}>
                                {p.portfolioCode || `#${p.portfolioId}`}
                                {p.portfolioName && (
                                  <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6 }}>{p.portfolioName}</span>
                                )}
                              </td>
                              <td>{fmtMoney(p.fundedPrincipal)}</td>
                              <td>{fmtMoney(p.unfundedCommitment)}</td>
                              <td>{fmtMoney(p.settledExposure)}</td>
                              <td style={{ color: p.pendingBuy ? '#fcd34d' : undefined }}>{fmtMoney(p.pendingBuy)}</td>
                              <td style={{ color: p.pendingSell ? '#fcd34d' : undefined }}>{fmtMoney(p.pendingSell)}</td>
                              <td>{fmtDate(p.positionDate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </div>
                  </TableShell>
                )}

              </>
            )}
            </FacilityReview>
            </>}

            <>
                {exposure.pendingTrades.length > 0 && (
                  <>
                    <SectionTitle description="Review pending transfers and resolve settlement issues in Middle Office.">
                      Unsettled Trades
                    </SectionTitle>
                    <Timeline>
                      {exposure.pendingTrades.map(t => (
                        <TimelineItem key={t.tradeId} $tone={t.settlementStatus === 'FAILED' ? 'red' : 'amber'}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            <strong>
                              Trade #{t.tradeId} · {t.side} {fmtMoney(t.par)}
                              {t.price !== null && t.price !== undefined ? ` @ ${Number(t.price).toFixed(3)}` : ''}
                            </strong>
                            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                              <StatusPill $tone={t.settlementStatus === 'FAILED' ? 'red' : 'amber'}>
                                {(t.settlementStatus || 'PENDING').replace(/_/g, ' ')}
                              </StatusPill>
                              <StatusPill $tone={t.legalTransferStatus === 'EFFECTIVE' ? 'emerald' : 'slate'}>
                                {(t.legalTransferStatus || 'PENDING CONSENT').replace(/_/g, ' ')}
                              </StatusPill>
                            </span>
                          </div>
                          <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 5 }}>
                            {t.portfolioCode || (t.portfolioId != null ? `#${t.portfolioId}` : '--')}
                            {' · '}traded {fmtDate(t.tradeDate)} · expected settle {fmtDate(t.expectedSettleDate)}
                            {' · '}pending settlement — forecast
                          </div>
                          {t.settlementExceptionReason && (
                            <div style={{ color: '#fca5a5', fontSize: 12, marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                              <AlertTriangle style={{ width: 13, height: 13, flex: 'none' }} />
                              {t.settlementExceptionReason}
                            </div>
                          )}
                          <div style={{ marginTop: 8 }}>
                            <WorkspaceButton onClick={() => navigate('/middle-office?tab=loan-settlement')}>
                              Open in Settlement Queue
                            </WorkspaceButton>
                          </div>
                        </TimelineItem>
                      ))}
                    </Timeline>
                  </>
                )}

                {!exposureUnavailable && !noExposure && attentionCount === 0 && exposure.pendingTrades.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '.8rem' }}>No pending settlement items returned.</p>
                )}
            </>

            {exposure.dataQualityExceptions.length > 0 && (
              <>
                <SectionTitle description="Resolve missing information before relying on the affected exposure.">Data quality exceptions</SectionTitle>
                <Timeline>
                  {exposure.dataQualityExceptions.map((dq, i) => (
                    <TimelineItem key={i} $tone="amber">
                      <strong>{dq.field || 'attribute'}</strong>
                      <div style={{ color: '#cbd5e1', marginTop: 4, fontSize: 13 }}>
                        {dq.portfolioCode ? `${dq.portfolioCode} — ` : ''}{dq.message}
                      </div>
                    </TimelineItem>
                  ))}
                </Timeline>
              </>
            )}
          </>
        )}
      </Frame>
    </>
  );
};

export default LoanFacilityCardDrawer;
